import { Alert, alpha, Box, Stack, type Theme } from "@mui/material";

import { Text } from "../../components/Text";
import { getBorderBase, getMutedText, getStrongText } from "../themeHelpers";
import { type SupportArticleGroup } from "../types";
import { HelpCenterLoadingState } from "./HelpCenterLoadingState";
import { SupportArticleCard } from "./SupportArticleCard";

function getSectionHeadingSx(theme: Theme, isMobile: boolean) {
  return {
    fontFamily: theme.typography.body1.fontFamily,
    fontSize: isMobile ? theme.typography.h6.fontSize : theme.typography.h5.fontSize,
    lineHeight: 1.2,
    color: getStrongText(theme),
  };
}

interface HelpCenterResultsSurfaceProps {
  articleGroups: SupportArticleGroup[];
  hasNoSearchResults: boolean;
  hasNoSupportArticles: boolean;
  isError: boolean;
  isLoading: boolean;
  isMobile: boolean;
  resultsTitle: string;
  shouldShowGroupTitles: boolean;
}

export function HelpCenterResultsSurface(props: HelpCenterResultsSurfaceProps) {
  const {
    articleGroups,
    hasNoSearchResults,
    hasNoSupportArticles,
    isError,
    isLoading,
    isMobile,
    resultsTitle,
    shouldShowGroupTitles,
  } = props;

  return (
    <Stack
      spacing={3}
      sx={(theme) => ({
        padding: theme.spacing(isMobile ? 3 : 4),
        borderRadius: theme.borderRadius?.xLarge,
        border: `1px solid ${getBorderBase(theme)}`,
        backgroundColor: theme.palette.background.paper,
        boxShadow: `0 0.75rem 2rem ${alpha(getStrongText(theme), 0.04)}`,
      })}
    >
      <Text bold variant="body1" sx={(theme) => getSectionHeadingSx(theme, isMobile)}>
        {resultsTitle}
      </Text>

      {isLoading && <HelpCenterLoadingState />}
      {isError && <Alert severity="error">Error loading support articles.</Alert>}
      {hasNoSupportArticles && <Text>No support articles are available right now.</Text>}
      {hasNoSearchResults && (
        <Text sx={(theme) => ({ color: getMutedText(theme) })}>
          No support articles match your current search.
        </Text>
      )}
      {!isLoading && !isError && articleGroups.length > 0 && (
        <Stack spacing={3}>
          {articleGroups.map((articleGroup) => (
            <Stack key={articleGroup.category} spacing={2}>
              {shouldShowGroupTitles ? (
                <Text bold variant="body1" sx={(theme) => getSectionHeadingSx(theme, isMobile)}>
                  {articleGroup.category}
                </Text>
              ) : null}
              <Box
                sx={(theme) => ({
                  overflow: "hidden",
                  borderRadius: theme.borderRadius?.xLarge,
                  border: `1px solid ${getBorderBase(theme)}`,
                  backgroundColor: theme.palette.background.paper,
                  boxShadow: `0 0.5rem 1.5rem ${alpha(getStrongText(theme), 0.03)}`,
                })}
              >
                {articleGroup.supportArticles.map((supportArticle, index) => (
                  <SupportArticleCard
                    key={supportArticle.id}
                    isEmbedded
                    isLast={index === articleGroup.supportArticles.length - 1}
                    supportArticle={supportArticle}
                  />
                ))}
              </Box>
            </Stack>
          ))}
        </Stack>
      )}
    </Stack>
  );
}
